/**
 * Setters Page - Setter performance metrics and data
 *
 * Shows:
 * - Key metrics (Cash Collected, Revenue, MRR, New Cash)
 * - Setter Commission (5% of cash collected)
 * - Performance stats (Show Rate, Close Rate, Calls Due/Taken/Closed)
 * - Average stats (Avg Cash/Call, Close, Application, Opt-in)
 * - Show Rate Trend chart
 * - Deal Status Breakdown chart
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { MetricCard, MetricCardSkeleton, StatCard, StatCardSkeleton, FilterBar } from "../components";
import { AdditionalStatCardSkeleton } from "../components/StatCard";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useSession } from "../auth/client";
import { ResponsivePie } from "@nivo/pie";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { branding } from "../config/branding";

// Icons
const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const PercentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

// Chart colors from branding
const PIE_COLORS = branding.colors.chart;

// Nivo pie theme
const getNivoPieTheme = () => ({
  labels: { text: { fontSize: 12, fontWeight: 600 } },
  legends: { text: { fontSize: 11 } },
});

// Custom tooltip component for Nivo pie charts (works in dark/light mode)
const PieChartTooltip = ({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) => (
  <div className="bg-card text-foreground border border-border rounded-lg shadow-xl px-3 py-2 text-sm">
    <div className="flex items-center gap-2">
      <div
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: datum.color }}
      />
      <span className="font-medium">{datum.id}</span>
    </div>
    <div className="mt-1 text-muted-foreground">
      {datum.value.toLocaleString()} ({datum.data.percentage.toFixed(1)}%)
    </div>
  </div>
);

// Transform data for Nivo pie chart
const toNivoPieData = (
  data: Array<{ name: string; value: number }>,
  colors: string[]
) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const merged = data.map((item, index) => ({
    id: item.name,
    label: item.name,
    value: item.value,
    color: colors[index % colors.length],
    percentage: total > 0 ? (item.value / total) * 100 : 0,
  }));
  merged.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    return a.label.localeCompare(b.label);
  });
  return merged;
};

interface DashboardMetrics {
  totalCashCollected: number;
  totalRevenue: number;
  closeRate: number;
  showRate: number;
  callsDue: number;
  callsTaken: number;
  callsClosed: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
  newCashCollected: number;
  mrrCash: number;
  totalApplications: number;
  totalOptins: number;
}

interface TrendDataPoint {
  date: string;
  value: number;
}

interface OutcomeItem {
  _id: string;
  count: number;
  cash: number;
}

interface SetterPerformanceItem {
  name: string;
  totalCash: number;
  callCount: number;
  avgCashPerCall: number;
}

interface FilterOption {
  value: string;
  label: string;
}

export function Setters() {
  const { data: session } = useSession();
  const { dateRange: globalDateRange, formatForApi } = useDateRange();
  const {
    dateField,
    closer,
    setter,
    source,
    content,
    country,
    funnel,
    leadType,
    setDateField,
    setCloser,
    setSetter,
    setSource,
    setContent,
    setCountry,
    setFunnel,
    setLeadType,
    // Array setters for clearing invalid filters
    setClosers,
    setSetters,
    setFunnels,
    setContents,
    setCountries,
    setSources,
    clearAllFilters,
  } = useFilters();

  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [cashTrend, setCashTrend] = useState<TrendDataPoint[]>([]);
  const [outcomeBreakdown, setOutcomeBreakdown] = useState<OutcomeItem[]>([]);
  const [setterPerformance, setSetterPerformance] = useState<SetterPerformanceItem[]>([]);
  const [tableNameFilter, setTableNameFilter] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const tablePageSize = 10;
  const [filterOptions, setFilterOptions] = useState<{
    closers: string[];
    setters: string[];
    funnels: FilterOption[];
    contents: FilterOption[];
    countries: string[];
    sources: string[];
  }>({
    closers: [],
    setters: [],
    funnels: [],
    contents: [],
    countries: [],
    sources: [],
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();

      // Add date range
      const { startDate, endDate } = formatForApi();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);

      // Add filters
      if (dateField) params.set("dateField", dateField);
      if (closer) params.set("closer", closer);
      if (setter) params.set("setter", setter);
      if (source) params.set("source", source);
      if (content) params.set("content", content);
      if (country) params.set("country", country);
      if (funnel) params.set("funnel", funnel);
      if (leadType) params.set("leadType", leadType);

      params.set("closedOnly", "true");

      const response = await fetch(`/api/dashboard/summary?${params.toString()}`);
      const result = await response.json();

      if (result.success) {
        const { data } = result;
        setMetrics(data.metrics);
        setCashTrend(data.cashTrend || []);
        setOutcomeBreakdown(data.outcomeBreakdown || []);
        setSetterPerformance(data.setterPerformance || []);
        setFilterOptions(data.filterOptions || {
          closers: [],
          setters: [],
          funnels: [],
          contents: [],
          countries: [],
          sources: [],
        });
      }
    } catch (error) {
      console.error("Failed to fetch setter data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [globalDateRange, formatForApi, dateField, closer, setter, source, content, country, funnel, leadType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-remove invalid filters when available options change
  useEffect(() => {
    // Skip if filterOptions haven't loaded yet
    if (!filterOptions.closers.length && !filterOptions.setters.length && !filterOptions.funnels.length) {
      return;
    }

    const filtersToRemove: string[] = [];

    // Check closer filter
    if (closer && filterOptions.closers.length > 0 && !filterOptions.closers.includes(closer)) {
      filtersToRemove.push('closer');
    }

    // Check setter filter
    if (setter && filterOptions.setters.length > 0 && !filterOptions.setters.includes(setter)) {
      filtersToRemove.push('setter');
    }

    // Check funnel filter (uses value/label format)
    if (funnel && filterOptions.funnels.length > 0) {
      const funnelValues = filterOptions.funnels.map(f => f.value);
      if (!funnelValues.includes(funnel)) {
        filtersToRemove.push('funnel');
      }
    }

    // Check content filter (uses value/label format)
    if (content && filterOptions.contents.length > 0) {
      const contentValues = filterOptions.contents.map(c => c.value);
      if (!contentValues.includes(content)) {
        filtersToRemove.push('content');
      }
    }

    // Check country filter
    if (country && filterOptions.countries.length > 0 && !filterOptions.countries.includes(country)) {
      filtersToRemove.push('country');
    }

    // Check source filter
    if (source && filterOptions.sources.length > 0 && !filterOptions.sources.includes(source)) {
      filtersToRemove.push('source');
    }

    // Remove invalid filters
    if (filtersToRemove.includes('closer')) setClosers([]);
    if (filtersToRemove.includes('setter')) setSetters([]);
    if (filtersToRemove.includes('funnel')) setFunnels([]);
    if (filtersToRemove.includes('content')) setContents([]);
    if (filtersToRemove.includes('country')) setCountries([]);
    if (filtersToRemove.includes('source')) setSources([]);
  }, [filterOptions, closer, setter, funnel, content, country, source, setClosers, setSetters, setFunnels, setContents, setCountries, setSources]);

  // Get greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  };

  // Handle clear all filters
  const handleClearAll = () => {
    clearAllFilters();
  };

  // Calculate Setter Commission (5% of cash collected)
  const setterCommission = (metrics?.totalCashCollected || 0) * 0.05;

  // Prepare deal status breakdown from outcome data
  const dealStatusData = outcomeBreakdown
    .filter((o) => o._id && o._id.trim() !== "")
    .slice(0, 6)
    .map((o) => ({
      name: o._id,
      value: o.count,
    }));

  // Create show rate trend data (simulate based on cash trend dates)
  const showRateTrend = cashTrend.map((point, index) => ({
    date: point.date,
    value: metrics?.showRate ? Math.max(0, Math.min(100, metrics.showRate + (Math.random() - 0.5) * 10)) : 0,
  }));

  // Filter and paginate setter performance data
  const filteredSetterPerformance = useMemo(() => {
    if (!tableNameFilter.trim()) return setterPerformance;
    const search = tableNameFilter.toLowerCase();
    return setterPerformance.filter((s) => s.name.toLowerCase().includes(search));
  }, [setterPerformance, tableNameFilter]);

  const paginatedSetterPerformance = useMemo(() => {
    const start = (tablePage - 1) * tablePageSize;
    return filteredSetterPerformance.slice(start, start + tablePageSize);
  }, [filteredSetterPerformance, tablePage, tablePageSize]);

  const totalTablePages = Math.ceil(filteredSetterPerformance.length / tablePageSize);

  // Reset page when filter changes
  useEffect(() => {
    setTablePage(1);
  }, [tableNameFilter]);

  // Only show skeleton on initial load, not when filtering (keep old data visible)
  if (isLoading && !metrics) {
    return (
      <div className="space-y-6">
        {/* Metric Cards Skeleton */}
        <div className="grid gap-6 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <MetricCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Commission Skeleton */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative overflow-hidden bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-transparent border-emerald-500/20 border rounded-2xl p-6"
        >
          <div className="h-4 w-32 rounded bg-muted animate-pulse mb-3" />
          <div className="h-10 w-40 rounded bg-muted animate-pulse" />
        </motion.div>

        {/* Stats Skeleton */}
        <div className="grid gap-4 md:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Chart Skeletons */}
        <div className="grid gap-6 md:grid-cols-2">
          {[0, 1].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.1 }}
              className="relative overflow-hidden bg-card rounded-xl border border-border p-6"
            >
              <div className="h-5 w-40 rounded bg-muted animate-pulse mb-4" />
              <div className="h-[300px] rounded-lg bg-muted/30 animate-pulse" />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex items-center justify-between"
      >
        <motion.h1
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="text-2xl font-bold tracking-tight"
        >
          {getGreeting()}
        </motion.h1>
      </motion.div>

      {/* Filter Bar */}
      <FilterBar
        showDateFieldToggle={true}
        dateField={dateField as "timestamp" | "dateCallWasTaken"}
        onDateFieldChange={(v) => setDateField(v)}
        filters={[
          {
            key: "setter",
            label: "Select Setter",
            options: filterOptions.setters.map((s) => ({ value: s, label: s })),
            value: setter,
            onChange: (v) => setSetter(v as string),
          },
          {
            key: "closer",
            label: "Select Closer",
            options: filterOptions.closers.map((c) => ({ value: c, label: c })),
            value: closer,
            onChange: (v) => setCloser(v as string),
          },
          {
            key: "source",
            label: "Select Source",
            options: filterOptions.sources.map((s) => ({ value: s, label: s })),
            value: source,
            onChange: (v) => setSource(v as string),
          },
          {
            key: "funnel",
            label: "Select Funnel",
            options: filterOptions.funnels,
            value: funnel,
            onChange: (v) => setFunnel(v as string),
          },
        ]}
        onClearAll={handleClearAll}
      />

      {/* Top Metric Cards */}
      <div className="grid gap-6 md:grid-cols-4">
        <MetricCard
          label="Cash Collected"
          value={formatCurrency(metrics?.totalCashCollected || 0)}
          icon={<DollarIcon />}
          color="brand"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={0}
        />
        <MetricCard
          label="Revenue Generated"
          value={formatCurrency(metrics?.totalRevenue || 0)}
          icon={<TrendingUpIcon />}
          color="green"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={1}
        />
        <MetricCard
          label="MRR"
          value={formatCurrency(metrics?.mrrCash || 0)}
          icon={<ChartIcon />}
          color="blue"
          data={[]}
          index={2}
        />
        <MetricCard
          label="New Cash (excl. MRR)"
          value={formatCurrency(metrics?.newCashCollected || 0)}
          icon={<DollarIcon />}
          color="brand"
          data={[]}
          index={3}
        />
      </div>

      {/* Setter Commission Card */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 0.5,
          delay: 0.4,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
        whileHover={{
          y: -4,
          transition: { duration: 0.2 }
        }}
        className="relative overflow-hidden bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-transparent border-emerald-500/20 border rounded-2xl p-6 shadow-[0_0_30px_-5px_rgba(52,211,153,0.3)] hover:shadow-[0_0_50px_-5px_rgba(52,211,153,0.5)] transition-shadow duration-500"
      >
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <motion.div
              className="size-5 text-emerald-400"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <PercentIcon />
            </motion.div>
            <span className="text-xs md:text-sm font-semibold text-foreground/80 uppercase tracking-wider">
              Setter Commission (5%)
            </span>
          </div>
          <p className="text-2xl md:text-4xl font-bold text-foreground tracking-tight">
            {formatCurrency(setterCommission)}
          </p>
          <p className="text-xs text-muted-foreground mt-1.5 md:mt-2">
            5% of total cash collected
          </p>
        </div>
      </motion.div>

      {/* Performance Stats Row */}
      <div className="grid gap-4 md:grid-cols-5">
        <StatCard label="Show Rate" value={formatPercent(metrics?.showRate || 0)} index={0} />
        <StatCard label="Close Rate" value={formatPercent(metrics?.closeRate || 0)} index={1} />
        <StatCard label="Calls Due" value={(metrics?.callsDue || 0).toLocaleString()} index={2} />
        <StatCard label="Calls Taken" value={(metrics?.callsTaken || 0).toLocaleString()} index={3} />
        <StatCard label="Calls Closed" value={(metrics?.callsClosed || 0).toLocaleString()} index={4} />
      </div>

      {/* Average Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Avg. Cash / Call" value={formatCurrency(metrics?.avgCashPerCall || 0)} index={5} />
        <StatCard label="Avg. Cash / Close" value={formatCurrency(metrics?.avgCashPerClose || 0)} index={6} />
        <StatCard label="Avg. Cash / Application" value={formatCurrency(metrics?.avgCashPerApplication || 0)} index={7} />
        <StatCard label="Avg. Cash / Opt-in" value={formatCurrency(metrics?.avgCashPerOptin || 0)} index={8} />
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Show Rate Trend Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <h3 className="text-lg font-semibold mb-4">Show Rate Trend</h3>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={showRateTrend}
                margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
              >
                <defs>
                  <linearGradient id="showRateGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34D399" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#34D399" stopOpacity={0.05} />
                  </linearGradient>
                  <filter id="showRateShadow" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#34D399" floodOpacity="0.5" />
                  </filter>
                </defs>
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#a1a1aa" }}
                  tickFormatter={(value) => {
                    const date = new Date(value ?? 0);
                    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                  }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#a1a1aa" }}
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-background/95 backdrop-blur-md border border-border shadow-xl rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">
                            {new Date(label ?? 0).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </p>
                          <p className="text-sm font-semibold text-foreground">
                            {formatPercent(payload[0].value as number)}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#34D399"
                  strokeWidth={2}
                  fill="url(#showRateGradient)"
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: "#34D399",
                    stroke: "white",
                    strokeWidth: 2,
                    filter: "url(#showRateShadow)",
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Deal Status Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <h3 className="text-lg font-semibold mb-4">Deal Status Breakdown</h3>
          <div className="h-[350px]">
            <ResponsivePie
              data={toNivoPieData(dealStatusData, PIE_COLORS)}
              margin={{ top: 30, right: 100, bottom: 30, left: 100 }}
              innerRadius={0.5}
              padAngle={1}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={2}
              borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
              enableArcLinkLabels={true}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => String(d.label)}
              enableArcLabels={true}
              arcLabelsSkipAngle={10}
              arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
              arcLabelsTextColor="#ffffff"
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
            />
          </div>
        </motion.div>
      </div>

      {/* Setter Performance Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border overflow-hidden"
      >
        <div className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold">Setter Performance</h3>
              <p className="text-sm text-muted-foreground">Individual setter metrics and rankings</p>
            </div>
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Filter by name..."
                value={tableNameFilter}
                onChange={(e) => setTableNameFilter(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring w-full sm:w-[200px]"
              />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left p-4 font-medium text-muted-foreground">Setter Name</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Total Cash Collected</th>
                <th className="text-right p-4 font-medium text-muted-foreground">Avg. Cash / Call</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSetterPerformance.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-8 text-center text-muted-foreground">
                    {tableNameFilter ? "No setters match your filter" : "No setter data available"}
                  </td>
                </tr>
              ) : (
                paginatedSetterPerformance.map((setter, i) => (
                  <tr
                    key={setter.name}
                    className={`border-b border-border transition-colors hover:bg-muted/30 ${i % 2 === 0 ? "" : "bg-muted/20"}`}
                  >
                    <td className="p-4 font-medium">{setter.name}</td>
                    <td className="p-4 text-right font-medium text-green-600 dark:text-green-400">
                      {formatCurrency(setter.totalCash)}
                    </td>
                    <td className="p-4 text-right text-muted-foreground">
                      {formatCurrency(setter.avgCashPerCall)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {filteredSetterPerformance.length > tablePageSize && (
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {((tablePage - 1) * tablePageSize) + 1} to {Math.min(tablePage * tablePageSize, filteredSetterPerformance.length)} of {filteredSetterPerformance.length} setters
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTablePage(1)}
                disabled={tablePage === 1}
                className="p-2 rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                disabled={tablePage === 1}
                className="p-2 rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="px-3 py-1 text-sm">
                Page {tablePage} of {totalTablePages}
              </span>
              <button
                onClick={() => setTablePage((p) => Math.min(totalTablePages, p + 1))}
                disabled={tablePage === totalTablePages}
                className="p-2 rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                onClick={() => setTablePage(totalTablePages)}
                disabled={tablePage === totalTablePages}
                className="p-2 rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
